import type { NoteEvent } from '@tunasong/music-lib'
import { useEffect, useRef, useState } from 'react'
import { usePlayNotes } from './piano-player.hook.js'

/** A scheduled set of NoteEvents fired at a given offset within an iteration. */
export interface Stab {
  events: NoteEvent[]
  /** Offset (ms) from the start of one iteration. */
  delayMs: number
}

export interface UseStabSchedulerProps {
  /** Pure function producing the stabs for one iteration. Called fresh each iteration. */
  buildStabs: () => Stab[]
  /** When set, the scheduler re-arms at this offset after the iteration's first stab. */
  loopIterationMs?: number
}

export interface StabScheduler {
  play: () => void
  stop: () => void
  isPlaying: boolean
}

/**
 * Schedules a list of chord-stabs via `setTimeout` and manages the lifecycle:
 *  - `play()` cancels any in-flight playback and starts a fresh iteration.
 *  - `stop()` cancels all pending timeouts.
 *  - When `loopIterationMs` is set, the scheduler re-arms itself at that offset
 *    (the new iteration's stabs are then scheduled relative to that re-arm).
 *  - Unmount cancels all pending timeouts.
 *
 * `buildStabs` is invoked once per iteration so closures see fresh prop values
 * across loops without forcing the consumer to memoise.
 */
export const useStabScheduler = (props: UseStabSchedulerProps): StabScheduler => {
  const { buildStabs, loopIterationMs } = props
  const { playNotes } = usePlayNotes()
  const timeoutsRef = useRef<number[]>([])
  const [isPlaying, setIsPlaying] = useState(false)

  const stop = () => {
    timeoutsRef.current.forEach(t => window.clearTimeout(t))
    timeoutsRef.current = []
    setIsPlaying(false)
  }

  const scheduleIteration = () => {
    const stabs = buildStabs()
    if (stabs.length === 0) {
      setIsPlaying(false)
      return
    }
    for (const stab of stabs) {
      const handle = window.setTimeout(() => playNotes(stab.events), stab.delayMs)
      timeoutsRef.current.push(handle)
    }
    if (loopIterationMs !== undefined) {
      const loopHandle = window.setTimeout(scheduleIteration, loopIterationMs)
      timeoutsRef.current.push(loopHandle)
    }
  }

  const play = () => {
    stop()
    setIsPlaying(true)
    scheduleIteration()
  }

  useEffect(() => stop, [])

  return { play, stop, isPlaying }
}
